import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  MagnifyingGlassIcon,
  PhoneIcon,
  UserGroupIcon,
  MapPinIcon,
} from '@heroicons/react/24/outline';
import ApiURL from '../Api/Api';
import Navbar from './Navbar';
import Sidebar from './Sidebar';
import { SidebarContext } from './SidebarContext';
import { fetchJson } from '../utils/http';

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' });
};

const AdminDetails = () => {
  const companyId = localStorage.getItem('company_id') || '';
  const [admins, setAdmins] = useState([]);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const { isSidebarOpen } = useContext(SidebarContext);

  useEffect(() => {
    const loadAdmins = async () => {
      try {
        const data = await fetchJson(`${ApiURL}/getAdminDetails.php?company_id=${companyId}`);
        if (data.status === 'success') {
          setAdmins(data.admins || []);
        } else {
          setError(data.message || 'Failed to load admins');
        }
      } catch (loadError) {
        setError(loadError.message);
      }
    };

    loadAdmins();
  }, [companyId]);

  const filteredAdmins = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return admins;
    return admins.filter((admin) =>
      [admin.user_name, admin.phone, ...(admin.assigned_sites || [])]
        .some((value) => String(value || '').toLowerCase().includes(query))
    );
  }, [admins, search]);

  return (
    <div className="app-shell min-h-screen pt-16">
      <Navbar />
      <Sidebar />
      <main className={`min-w-0 p-4 sm:p-6 transition-all duration-300 ${isSidebarOpen ? 'lg:ml-72' : ''}`}>
        <div className="page-surface p-4 sm:p-5">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-brand-600">Admin Directory</p>
              <h1 className="mt-2 text-3xl font-bold text-slate-900">Company Admin Details</h1>
              <p className="mt-2 max-w-2xl text-sm text-slate-500">
                Search admins, review assigned sites, and quickly check when each admin record was created.
              </p>
            </div>
            <div className="relative w-full max-w-sm">
              <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search admin, phone, or site"
                className="h-11 w-full rounded-lg border border-slate-200 bg-white pl-10 pr-3 text-sm font-medium text-slate-700 placeholder:text-slate-400 outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
              />
            </div>
          </div>
        </div>

        {error ? (
          <div className="mt-5 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-medium text-rose-700">
            {error}
          </div>
        ) : null}

        <div className="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {filteredAdmins.map((admin) => (
            <div key={admin.id} className="pro-card p-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="inline-flex items-center rounded-full border border-brand-200 bg-brand-50 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-brand-700">
                    Admin
                  </div>
                  <h2 className="mt-3 text-xl font-semibold text-slate-900">{admin.user_name}</h2>
                </div>
                <div className="rounded-2xl bg-slate-100 p-2 text-slate-700">
                  <UserGroupIcon className="h-5 w-5" />
                </div>
              </div>

              <div className="mt-5 space-y-3 text-sm text-slate-600">
                <div className="flex items-center gap-2">
                  <PhoneIcon className="h-4 w-4 text-slate-400" />
                  <span>{admin.phone || '-'}</span>
                </div>
                <div className="flex items-start gap-2">
                  <MapPinIcon className="mt-0.5 h-4 w-4 text-slate-400" />
                  <span>{admin.assigned_sites?.join(', ') || '-'}</span>
                </div>
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400">Created At</p>
                  <p className="mt-1 font-medium text-slate-700">{formatDateTime(admin.created_date)}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
};

export default AdminDetails;
